'use client';

import Image from 'next/image';
import Link from 'next/link';

import { appRoutes } from '@/appRoutes';
import animatedAllWhiteLogo from '@/assets/netnet-animated-logo-all-white.svg';
import { NotificationsTray } from '@/features/notifications';
import { BillingAccessCTA } from '@/features/subscription/components';
import { TimeTracker } from '@/features/time-entries/time-tracker';
import { ColorDot, MenuButton, ThemeToggle, Divider } from '@web/ui';

import { ChatIcon, MeIcon, SettingsIcon } from './icons';
import styles from './Topbar.module.scss';

import type { ReactNode } from 'react';

type TopbarProps = {
  workspaceSlug: string;
  onOpenMenu?: () => void;
  hasUnreadChat?: boolean;
  contextTabs?: ReactNode;
};

export function Topbar({
  workspaceSlug,
  onOpenMenu,
  hasUnreadChat = false,
  contextTabs,
}: TopbarProps) {
  return (
    <>
      {/* // * Desktop topbar */}
      <header className={styles.topbar} aria-label="Top bar navigation">
        <div className={styles.section}>
          <Image
            src={animatedAllWhiteLogo}
            alt="NetNet Logo"
            width={120}
            height={120}
          />
        </div>

        <div className={`${styles.section} ${styles.tabsSection}`}>
          {contextTabs}
        </div>

        <div className={styles.section}>
          <TimeTracker viewport="desktop" />

          <BillingAccessCTA variant="pill" />

          <span className={styles.divider} aria-hidden="true">
            <Divider orientation="vertical" />
          </span>

          <NotificationsTray viewport="desktop" />

          <ThemeToggle />
        </div>
      </header>

      {/* // * Mobile bottom bar with hamburger */}
      <nav className={styles.mobileBar} aria-label="Bottom bar navigation">
        <Link
          className={styles.mobileItem}
          href={appRoutes.workspace.myTasks(workspaceSlug).index}
          aria-label="Me"
          prefetch
        >
          <MeIcon />
        </Link>

        <Link
          className={styles.mobileItem}
          href={appRoutes.workspace.settings(workspaceSlug).team.index}
          aria-label="Settings"
          prefetch
        >
          <SettingsIcon />
        </Link>

        <Link
          className={styles.mobileItem}
          href={appRoutes.workspace.chat(workspaceSlug).index}
          aria-label="Chat"
          prefetch
        >
          <ChatIcon />
          {hasUnreadChat ? (
            <span
              className={styles.mobileItemIndicator}
              aria-hidden="true"
              data-testid="mobile-chat-unread-indicator"
            >
              <ColorDot color="var(--nn-color-primary)" size="lg" />
            </span>
          ) : null}
        </Link>

        <span className={styles.mobileControlSlot}>
          <TimeTracker viewport="mobile" />
        </span>

        <span className={styles.mobileControlSlot}>
          <NotificationsTray viewport="mobile" buttonVariant="medium" />
        </span>

        <span className={styles.mobileMenuSlot}>
          <MenuButton onClick={onOpenMenu} />
        </span>
      </nav>
    </>
  );
}
